import type { Metadata } from "next";

// Production canonical host. Update if the production domain changes.
// Override locally with NEXT_PUBLIC_SITE_URL if needed.
const PROD_URL = process.env.NEXT_PUBLIC_SITE_URL ?? "https://chikitsa.io";

export const SITE = {
  name: "Chikitsa",
  /** Legal entity behind the Chikitsa product. Keep consistent with schema and legal pages. */
  legalName: "Vigorus Healthtech Private Limited",
  url: PROD_URL,
  /**
   * Default title used on the root layout and as the fallback.
   * Audit recommendation: include both HIMS and HMIS variant signals.
   */
  defaultTitle:
    "AI-Powered Hospital Management Software in India | Chikitsa",
  defaultDescription:
    "Chikitsa is an AI-powered HIMS/HMIS for hospitals in India — ABDM-ready, NHCX/PMJAY-integrated with EMR, billing, pharmacy, lab, claims, analytics and secure audit trails. Trusted by 150+ hospitals.",
  // 1200x630 OG image. Drop a real asset at /public/og.png for production.
  ogImage: "/og.png",
  twitter: "@chikitsahims",
  locale: "en_IN",
  brandColor: "#053C61",
  /** NAP (Name / Address / Phone) — keep consistent with footer, About, Contact, and schema. */
  contact: {
    email: "connect@chikitsa.io",
    phone: "+91-9509917266",
    locations: [
      {
        name: "Jaipur Corporate Office",
        streetAddress: "Creware Coworks, Business Park, Near Khatu Shyam ji temple, Isckon Road, New Sanganer Rd",
        addressLocality: "Jaipur",
        addressRegion: "Rajasthan",
        postalCode: "302020",
        addressCountry: "IN",
      },
      {
        name: "Pune Corporate Office",
        streetAddress: "Sprint Tower, Plot no. 9, Hinjawadi Phase 1 Rd, Phase 1, Hinjawadi Rajiv Gandhi Infotech Park",
        addressLocality: "Pune",
        addressRegion: "Maharashtra",
        postalCode: "411057",
        addressCountry: "IN",
      },
      {
        name: "Registered Office",
        streetAddress: "First Floor, Plot no-562 A, Kings Road, Nirman Nagar",
        addressLocality: "Jaipur",
        addressRegion: "Rajasthan",
        postalCode: "302019",
        addressCountry: "IN",
      }
    ]
  },
  socials: {
    linkedin:
      "https://www.linkedin.com/company/vigorus-healthtech-private-limited-chikitsa/",
  },
} as const;

type PageMetaInput = {
  path: string;
  title: string;
  description: string;
  keywords?: string[];
  /** Use the title verbatim (no "%s — Chikitsa" suffix). For the home page. */
  absoluteTitle?: boolean;
};

export function pageMeta({
  path,
  title,
  description,
  keywords,
  absoluteTitle,
}: PageMetaInput): Metadata {
  const canonical = new URL(path, SITE.url).toString();
  const ogTitle = absoluteTitle ? title : `${title} — ${SITE.name}`;

  return {
    title: absoluteTitle ? { absolute: title } : title,
    description,
    keywords: keywords?.join(", "),
    alternates: { canonical },
    openGraph: {
      title: ogTitle,
      description,
      url: canonical,
      siteName: SITE.name,
      type: "website",
      locale: SITE.locale,
      images: [
        { url: SITE.ogImage, width: 1200, height: 630, alt: ogTitle },
      ],
    },
    twitter: {
      card: "summary_large_image",
      site: SITE.twitter,
      creator: SITE.twitter,
      title: ogTitle,
      description,
      images: [SITE.ogImage],
    },
  };
}
